import { useEffect } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { MatchPublic, PredictionWithUser } from '@mondy/shared';
import { canViewPredictionsEarly, isKnockoutStage, isMatchLive, scoreMatch } from '@mondy/shared';
import { ApiError } from '@mondy/shared/api-client';
import { api } from '@/api';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useMyGroups } from '@/hooks/useMyGroups';
import { fallbackScopeFor, useActiveGroup, useSetActiveGroup } from '@/state/activeGroup';
import { TeamFlag } from '@/components/TeamFlag';
import { TierBadge } from '@/components/TierBadge';
import { ViewPredictionsEarlyUpgradeCTA } from '@/components/ViewPredictionsEarlyUpgradeCTA';

interface MatchPredictionsResponse {
  predictions: PredictionWithUser[];
  revealed: boolean;
}

export function MatchPredictions({ match }: { match: MatchPublic }) {
  const me = useCurrentUser();
  const groupsQuery = useMyGroups();
  const activeGroupId = useActiveGroup();
  const setActiveGroupId = useSetActiveGroup();
  const groups = groupsQuery.data?.groups ?? [];
  const hasGroups = groups.length > 0;
  const isAdmin = me?.role === 'admin';

  // Per-match predictions don't support 'global' (the server would have to
  // fan out across the whole user base for one match). Translate to 'all'
  // — union of every group the requester is in — to preserve the existing
  // semantics: "everyone in my groups who picked this match".
  const matchScopeId = activeGroupId === 'global' ? 'all' : activeGroupId;

  const query = useQuery({
    queryKey: ['predictions', 'all', match.id, matchScopeId],
    queryFn: () =>
      api.get<MatchPredictionsResponse>(
        `/api/predictions/match/${match.id}/all?groupId=${encodeURIComponent(matchScopeId)}`,
      ),
    enabled: hasGroups,
    // Refresh while the match is live or paused so provisional points keep
    // tracking the score (e.g. half-time still shows current standings).
    refetchInterval: hasGroups && isMatchLive(match) ? 30_000 : false,
  });

  // Fall back to the role-appropriate default if the persisted groupId
  // became stale server-side. Non-admins must not land back on `'global'`,
  // which the server now rejects for them.
  useEffect(() => {
    if (!query.isError) return;
    const err = query.error;
    if (err instanceof ApiError && err.status === 403 && err.code === 'FORBIDDEN') {
      const fallback = fallbackScopeFor(!!isAdmin, groups.map((g) => g.id));
      if (activeGroupId !== fallback) setActiveGroupId(fallback);
    }
  }, [query.isError, query.error, activeGroupId, isAdmin, groups, setActiveGroupId]);

  if (!hasGroups) {
    return (
      <section className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-[color:var(--color-fg-muted)]">
          Predictions
        </h2>
        <p className="mt-3 text-center text-sm text-[color:var(--color-fg-muted)]">
          You're not in any group yet.{' '}
          <Link
            to="/profile"
            className="text-[color:var(--color-fg)] underline underline-offset-2"
          >
            Create or join one
          </Link>{' '}
          to see everyone's predictions.
        </p>
      </section>
    );
  }

  if (!query.data || !query.data.revealed) {
    // The CTA renders nothing for Gold+ users (canViewPredictionsEarly), in
    // which case we fall back to the original lock message — covers the
    // edge case where revealed:false slipped past the server gate.
    const showCta = !!me && !canViewPredictionsEarly(me.paymentTier);
    return (
      <section className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-[color:var(--color-fg-muted)]">
          Predictions
        </h2>
        {showCta ? (
          <ViewPredictionsEarlyUpgradeCTA />
        ) : (
          <p className="mt-3 text-center text-sm text-[color:var(--color-fg-muted)]">
            🔒 Predictions revealed at kickoff
          </p>
        )}
      </section>
    );
  }

  if (query.isLoading || query.isError) return null;

  const predictions = query.data!.predictions;
  if (predictions.length === 0) return null;

  const isKnockout = isKnockoutStage(match.stage);
  const rows = predictions.map((p) => {
    const locked = p.pointsAwarded;
    const provisional =
      locked == null && isMatchLive(match) && match.result
        ? scoreMatch(p.prediction, match.result, isKnockout).total
        : null;
    return { p, locked, provisional };
  });

  // Sort so the highest current score floats to the top, with finished points
  // taking precedence over provisional, then alphabetical.
  rows.sort((a, b) => {
    const aScore = a.locked ?? a.provisional ?? -1;
    const bScore = b.locked ?? b.provisional ?? -1;
    if (bScore !== aScore) return bScore - aScore;
    return a.p.displayName.localeCompare(b.p.displayName);
  });

  return (
    <section className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-[color:var(--color-fg-muted)]">
        Predictions
      </h2>
      <ul className="mt-3 flex flex-col gap-1">
        {rows.map(({ p, locked, provisional }) => (
          <li
            key={p.id}
            className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-sm"
          >
            <div className="flex min-w-0 items-center gap-3">
              <TeamFlag url={p.favoriteTeamFlagUrl} name={p.displayName} />
              <span className="truncate">{p.displayName}</span>
              <TierBadge tier={p.paymentTier} />
            </div>
            <div className="flex items-center gap-2">
              <PredictionScore prediction={p.prediction} />
              <PointsBadge locked={locked} provisional={provisional} />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PointsBadge({
  locked,
  provisional,
}: {
  locked: number | null;
  provisional: number | null;
}) {
  if (locked != null) {
    return (
      <span
        className={`min-w-[2rem] rounded-md px-1.5 py-0.5 text-center text-xs font-semibold tabular-nums ${
          locked >= 3
            ? 'bg-green-500/15 text-green-700 dark:text-green-400'
            : locked >= 1
              ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
              : 'bg-[color:var(--color-bg)] text-[color:var(--color-fg-muted)]'
        }`}
      >
        +{locked}
      </span>
    );
  }
  if (provisional != null) {
    return (
      <span
        title="Provisional — updates as the score changes"
        className={`min-w-[2rem] rounded-md border border-dashed px-1.5 py-0.5 text-center text-xs font-semibold tabular-nums ${
          provisional >= 3
            ? 'border-green-500/40 text-green-700 dark:text-green-400'
            : provisional >= 1
              ? 'border-amber-500/40 text-amber-700 dark:text-amber-400'
              : 'border-[color:var(--color-border)] text-[color:var(--color-fg-muted)]'
        }`}
      >
        +{provisional}
      </span>
    );
  }
  return null;
}


function PredictionScore({
  prediction,
}: {
  prediction: PredictionWithUser['prediction'];
}) {
  return (
    <div className="flex flex-col items-end text-xs tabular-nums">
      <span className="font-medium">
        {prediction.fullTime.team1}–{prediction.fullTime.team2}
      </span>
      {prediction.extraTime && (
        <span className="text-[color:var(--color-fg-muted)]">
          ET {prediction.extraTime.team1}–{prediction.extraTime.team2}
        </span>
      )}
      {prediction.penalties && (
        <span className="text-[color:var(--color-fg-muted)]">
          Pens {prediction.penalties.team1}–{prediction.penalties.team2}
        </span>
      )}
    </div>
  );
}
